import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface CrmHttpError {
  crmCode:
    | 'BAD_REQUEST'
    | 'UNAUTHORIZED'
    | 'FORBIDDEN'
    | 'NOT_FOUND'
    | 'CONFLICT'
    | 'VALIDATION_ERROR'
    | 'SERVER_ERROR'
    | 'OFFLINE'
    | 'UNKNOWN';
  crmMessage: string;
}

@Injectable()
export class CrmErrorInterceptor implements HttpInterceptor {
  private readonly recentNotifications = new Map<string, number>();
  private readonly notificationWindowMs = 2500;
  private navigatingToLogin = false;

  constructor(private toastr: ToastrService, private router: Router) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        // CRM gateway requests only. Do not change error behavior for other modules.
        const isCrmRequest = /\/CRM(?:\/|\?|$)/i.test(req.url);
        if (!isCrmRequest || !(error instanceof HttpErrorResponse)) {
          return throwError(() => error);
        }

        let crmCode: CrmHttpError['crmCode'] = 'UNKNOWN';
        let crmMessage = 'Something went wrong. Please try again.';
        const apiMessage = this.extractApiMessage(error);

        if (error.status === 0) {
          crmCode = 'OFFLINE';
          crmMessage = 'Unable to connect. Check your network and use Retry.';
        } else if (error.status === 400) {
          crmCode = 'BAD_REQUEST';
          crmMessage = apiMessage || 'Please correct the highlighted fields.';
        } else if (error.status === 401) {
          crmCode = 'UNAUTHORIZED';
          crmMessage = 'Your session has expired. Please log in again.';
        } else if (error.status === 403) {
          crmCode = 'FORBIDDEN';
          crmMessage = 'You do not have permission to perform this action.';
        } else if (error.status === 404) {
          crmCode = 'NOT_FOUND';
          crmMessage = 'The requested resource was not found.';
        } else if (error.status === 409) {
          crmCode = 'CONFLICT';
          crmMessage = 'This record was changed by someone else or conflicts with existing data. Refresh it and use the action button again.';
        } else if (error.status === 422) {
          crmCode = 'VALIDATION_ERROR';
          crmMessage = apiMessage || 'Please correct the highlighted fields.';
        } else if (error.status >= 500) {
          crmCode = 'SERVER_ERROR';
          crmMessage = 'The server could not complete this request. Please use Retry.';
        }

        const crmError = Object.assign(
          new HttpErrorResponse({
            error: error.error,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url || undefined
          }),
          { crmCode, crmMessage }
        );

        // One offline/server outage should produce one toast, not one per failed widget.
        const notificationKey = (error.status === 0 || error.status >= 500)
          ? `${crmCode}:${crmMessage}`
          : `${error.status}:${req.method}:${req.url}:${crmMessage}`;
        this.showOnce(notificationKey, crmMessage);

        if (error.status === 401 && !this.navigatingToLogin) {
          this.navigatingToLogin = true;
          // Do not clear all localStorage: that may erase unrelated app state.
          localStorage.removeItem('access_token');
          localStorage.removeItem('userObject');
          localStorage.removeItem('menuList');
          void this.router.navigate(['/login']).finally(() => {
            this.navigatingToLogin = false;
          });
        }

        return throwError(() => crmError);
      })
    );
  }

  private extractApiMessage(error: HttpErrorResponse): string {
    const body: any = error.error;
    const message = body?.detail ?? body?.Detail ?? body?.message ?? body?.Message;
    if (typeof message === 'string' && message.trim()) return message.trim();
    const errors = body?.errors ?? body?.Errors;
    if (errors && typeof errors === 'object') {
      const first = Object.values(errors).flat().find((value) => typeof value === 'string');
      if (typeof first === 'string') return first;
    }
    return '';
  }

  private showOnce(key: string, message: string): void {
    const now = Date.now();
    for (const [oldKey, timestamp] of this.recentNotifications) {
      if (now - timestamp > this.notificationWindowMs) this.recentNotifications.delete(oldKey);
    }
    if (this.recentNotifications.has(key)) return;
    this.recentNotifications.set(key, now);
    this.toastr.error(message, 'CRM');
  }
}
